import React from "react";
import { useDispatch } from "react-redux";
import { OWNERS_QNT } from "@components/Features/catalog/catalog-utils";
import FilterDoubleInput from "@components/UI/Inputs/FilterDoubleInput/FilterDoubleInput";
import FilterLineInput from "@components/UI/Inputs/FilterLineInput/FilterLineInput";
import SelectInput from "@components/UI/Inputs/SelectInput/SelectInput";
import TextInput from "@components/UI/Inputs/TextInput/TextInput";
import {
    setBody,
    setDrive,
    setEngineType,
    setEngineVolumeFrom,
    setEngineVolumeTo,
    setHideNoImages,
    setHorsePowerFrom,
    setHorsePowerTo,
    setOwnersQntFrom,
    setOwnersQntTo,
    setSeller,
    setTransmission,
    setWheel
} from "@app/store/filtersSlice";
import { ColorInput, ColorInputAccent } from "@app/styles/color-palette";
import { Checkbox } from "@shared/ui/Checkbox";
import { FILTER_LABELS } from "../constants";
import { SelectValue } from "../types";
import { ExpandedFiltersProps } from "./types";
import classes from "../MainFilter.module.scss";

export const ExpandedFilters: React.FC<ExpandedFiltersProps> = ({
    filterValues,
    filterOptions,
    customSelectComponents,
    selectInputFonts,
    createSelectValue
}) => {
    const dispatch = useDispatch();
    const createSelect = (config: {
        instanceId: string;
        placeholder: string;
        title: string;
        value: SelectValue | undefined;
        onChange: (value: SelectValue | null) => void;
        isLoading?: boolean;
        options?: { value: string; label: string }[];
        width?: string;
    }) => (
        <SelectInput
            width={config.width || "100%"}
            instanceId={config.instanceId}
            className={classes.Select}
            components={customSelectComponents}
            placeholder={config.placeholder}
            title={config.title}
            isClearable={true}
            value={config.value}
            onChange={config.onChange}
            isLoading={config.isLoading}
            options={config.options || []}
            borderColor={ColorInput}
            fontStyles={selectInputFonts}
        />
    );
    const mapOptions = (items: string[]) => items.map((item) => ({ value: item, label: item }));

    return (
        <div className={classes.ExpandedFilters}>
            <div className={classes.ExpandedFilter}>
                <FilterDoubleInput
                    firstLabel={FILTER_LABELS.BODY}
                    secondLabel={FILTER_LABELS.TRANSMISSION}
                    firstInput={createSelect({
                        instanceId: "kuzov-auto",
                        placeholder: FILTER_LABELS.ANY,
                        title: FILTER_LABELS.BODY,
                        value: createSelectValue(filterValues.bodyValue),
                        onChange: (value) => dispatch(setBody(value?.value)),
                        isLoading: filterOptions.bodyOptionsIsFetching,
                        options: filterOptions.bodyOptionsIsFetching
                            ? []
                            : mapOptions(filterOptions.bodyOptions || [])
                    })}
                    secondInput={createSelect({
                        instanceId: "transmission-auto",
                        placeholder: FILTER_LABELS.ANY_FEMALE,
                        title: FILTER_LABELS.TRANSMISSION,
                        value: createSelectValue(filterValues.transmissionValue),
                        onChange: (value) => dispatch(setTransmission(value?.value)),
                        isLoading: filterOptions.transmissionOptionsIsFetch,
                        options: filterOptions.transmissionOptionsIsFetch
                            ? []
                            : mapOptions(filterOptions.transmissionOptions || [])
                    })}
                />
            </div>
            <div className={classes.ExpandedFilter}>
                <FilterDoubleInput
                    firstLabel={FILTER_LABELS.ENGINE_TYPE}
                    secondLabel={FILTER_LABELS.DRIVE}
                    firstInput={createSelect({
                        instanceId: "engine-auto",
                        placeholder: FILTER_LABELS.ANY,
                        title: FILTER_LABELS.ENGINE_TYPE,
                        value: createSelectValue(filterValues.engineTypeValue),
                        onChange: (value) => dispatch(setEngineType(value?.value)),
                        isLoading: filterOptions.engineTypesIsFetching,
                        options: filterOptions.engineTypesIsFetching
                            ? []
                            : mapOptions(filterOptions.engineTypesOptions || [])
                    })}
                    secondInput={createSelect({
                        instanceId: "privod-auto",
                        placeholder: FILTER_LABELS.ANY,
                        title: FILTER_LABELS.DRIVE,
                        value: createSelectValue(filterValues.driveValue),
                        onChange: (value) => dispatch(setDrive(value?.value)),
                        isLoading: filterOptions.driveTypesIsFetching,
                        options: filterOptions.driveTypesIsFetching
                            ? []
                            : mapOptions(filterOptions.driveTypesOptions || [])
                    })}
                />
            </div>

            <div className={classes.ExpandedFilter}>
                <FilterDoubleInput
                    firstLabel="Объем двигателя"
                    secondLabel=""
                    firstInput={createSelect({
                        instanceId: "engine-volume-auto",
                        placeholder: FILTER_LABELS.ENGINE_VOLUME_FROM,
                        title: FILTER_LABELS.ENGINE_VOLUME,
                        value: createSelectValue(filterValues.engineVolumeFromValue),
                        onChange: (value) => dispatch(setEngineVolumeFrom(value?.value)),
                        isLoading: filterOptions.displacementsIsFetching,
                        options: filterOptions.displacementsIsFetching
                            ? []
                            : mapOptions(filterOptions.displacementsOptions || [])
                    })}
                    secondInput={createSelect({
                        instanceId: "engine-vol-to",
                        placeholder: FILTER_LABELS.ENGINE_VOLUME_TO,
                        title: FILTER_LABELS.ENGINE_VOLUME,
                        value: createSelectValue(filterValues.engineVolumeToValue),
                        onChange: (value) => dispatch(setEngineVolumeTo(value?.value)),
                        isLoading: filterOptions.displacementsIsFetching,
                        options: filterOptions.displacementsIsFetching
                            ? []
                            : mapOptions(filterOptions.displacementsOptions || [])
                    })}
                />
            </div>
            <div className={classes.ExpandedFilter}>
                <FilterDoubleInput
                    firstLabel={FILTER_LABELS.HORSE_POWER}
                    secondLabel=""
                    firstInput={
                        <TextInput
                            placeholder={FILTER_LABELS.HORSE_POWER_FROM}
                            value={filterValues.horsePowerFromValue?.toString() || ""}
                            onChange={(value) => dispatch(setHorsePowerFrom(value))}
                            type="number"
                            maxLength={4}
                            style={{ color: ColorInputAccent }}
                        />
                    }
                    secondInput={
                        <TextInput
                            placeholder={FILTER_LABELS.HORSE_POWER_TO}
                            value={filterValues.horsePowerToValue?.toString() || ""}
                            onChange={(value) => dispatch(setHorsePowerTo(value))}
                            type="number"
                            maxLength={4}
                            style={{ color: ColorInputAccent }}
                        />
                    }
                />
            </div>

            <div className={classes.ExpandedFilter}>
                <FilterLineInput
                    label={FILTER_LABELS.WHEEL}
                    component={createSelect({
                        instanceId: "rul-auto",
                        placeholder: FILTER_LABELS.ANY,
                        title: FILTER_LABELS.WHEEL,
                        value: createSelectValue(filterValues.wheelValue),
                        onChange: (value) => dispatch(setWheel(value?.value)),
                        isLoading: filterOptions.wheelsIsFetching,
                        options: filterOptions.wheelsIsFetching
                            ? []
                            : mapOptions(filterOptions.wheelsOptions || [])
                    })}
                />
            </div>

            <div className={classes.ExpandedFilter}>
                <FilterDoubleInput
                    firstLabel={FILTER_LABELS.OWNERS_QNT}
                    secondLabel=""
                    firstInput={createSelect({
                        instanceId: "owners-qnt-auto",
                        placeholder: FILTER_LABELS.OWNERS_QNT_FROM,
                        title: FILTER_LABELS.OWNERS_QNT,
                        value: createSelectValue(filterValues.ownerQntFrom),
                        onChange: (value) => dispatch(setOwnersQntFrom(value?.value)),
                        options: OWNERS_QNT
                    })}
                    secondInput={createSelect({
                        instanceId: "owners-qnt-to-auto",
                        placeholder: FILTER_LABELS.OWNERS_QNT_TO,
                        title: FILTER_LABELS.OWNERS_QNT,
                        value: createSelectValue(filterValues.ownersQntTo),
                        onChange: (value) => dispatch(setOwnersQntTo(value?.value)),
                        options: OWNERS_QNT
                    })}
                />
            </div>

            <div className={classes.ExpandedFilter}>
                <FilterLineInput
                    label={FILTER_LABELS.SELLER_TYPE}
                    component={createSelect({
                        instanceId: "seller-type-auto",
                        placeholder: FILTER_LABELS.ANY,
                        title: FILTER_LABELS.SELLER_TYPE,
                        value: createSelectValue(filterValues.sellerType),
                        onChange: (value) => dispatch(setSeller(value?.value)),
                        isLoading: filterOptions.sellerTypesIsFetching,
                        options: filterOptions.sellerTypesOptions
                            ? mapOptions(filterOptions.sellerTypesOptions)
                            : []
                    })}
                />
            </div>

            <div className={classes.ExpandedFilter}>
                <Checkbox
                    className={classes.Checkbox}
                    id="hide-no-images"
                    label={FILTER_LABELS.HIDE_NO_IMAGES}
                    checked={filterValues.hideNoImages ?? false}
                    onChange={() => dispatch(setHideNoImages(!filterValues.hideNoImages))}
                />
            </div>
        </div>
    );
};